import React, { useEffect, useState, useRef } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { BudgetBar } from '@/components/common/BudgetBar';
import { Alert } from '@/components/ui/Alert';
import { useCampaignSettingsGoogleAdsStore } from '@/store/campaignSettingsGoogleAdsStore';
import { useCampaignSettingsMetaAdsStore } from '@/store/campaignSettingsMetaAdsStore';
import { usePendingChangesStore } from '@/store/pendingChangesStore';
import {
  useAccountSettingsStore,
  PROD_MIN_DENSITY,
  PROD_TARGET_ABS_DENSITY_CHANGE,
} from '@/store/accountSettingsStore';
import { useAuthStore } from '@/store/authStore';
import { usePoolCurrency } from '@/hooks/usePoolCurrency';
import {
  useOptimizedCampaigns,
  type OptimizerPlatform,
  type OptimizedCampaignEntry,
} from '@/hooks/useOptimizedCampaigns';
import { saveAppConfig, loadAppConfig } from '@/services/appConfigService';
import { optimizationApi } from '@/services/api/optimization';
import { googleAdsApi } from '@/services/api/googleAds';
import { debug } from '@/utils/debug';
import { formatCurrency } from '@/utils/format';
import { isBudgetAligned } from '@/utils/budgetAlignment';
import { cn } from '@/utils/cn';
import { ROAS_CONFIG } from '@/config/constants';
import type { Campaign } from '@/types/campaign';
import type { BudgetAllocationResult } from '@/store/campaignSettingsGoogleAdsStore';
import { AllocatableBudgetTooltipContent } from './AllocatableBudgetTooltipContent';
import { useTooltipHoverState } from './useTooltipHoverState';
import '@/styles/tooltip.css';

// PROD_MIN_DENSITY / PROD_TARGET_ABS_DENSITY_CHANGE are imported from
// accountSettingsStore (canonical owner now that both values are persisted
// account-level settings). Used here only as clamp fallbacks when the dev
// input is unparseable (empty / non-numeric); the persisted values from the
// account store are the primary source.

const clampNumericInput = (
  raw: number,
  min: number,
  max: number,
  fallback: number,
): number => {
  // Substitute fallback when raw is NaN/Infinity, then clamp the result
  // to [min, max]. Clamping the fallback too matters when callers pass a
  // production-default fallback (e.g., PROD_MIN_DENSITY=0.05) that may
  // sit outside the caller's dynamic range (e.g., max = 1/N for N≥21 is
  // <0.05). Without clamping the fallback we'd return an out-of-range
  // value and silently violate the helper's stated [min, max] contract.
  const candidate = Number.isFinite(raw) ? raw : fallback;
  return Math.max(min, Math.min(max, candidate));
};

interface BudgetOptimizerSimpleProps {
  campaigns: Campaign[];
  onCampaignsUpdate?: () => void | Promise<void>;
}

export const BudgetOptimizerSimple: React.FC<BudgetOptimizerSimpleProps> = ({ campaigns, onCampaignsUpdate }) => {
  const {
    config,
    isOptimizing,
    error,
    optimizationResults,
    setConfig,
    setError,
    clearError,
    initializeCampaignSettings,
    setOptimizing,
    setOptimizationResults,
    setManualBudget: setManualBudgetGoogle,
    getTotalMinimumAllocations,
  } = useCampaignSettingsGoogleAdsStore();
  // Phase 1c — Meta-side budget setter for the Apply-Changes path. The
  // cross-store hook tags each entry with its platform; we dispatch
  // each result back to the right store's setManualBudget. Meta
  // entries additionally queue a budget change into pendingChangesStore
  // (Google flushes via preSave, Meta via the Meta batch endpoint).
  const setManualBudgetMeta = useCampaignSettingsMetaAdsStore((s) => s.setManualBudget);
  const queueCampaignChange = usePendingChangesStore((s) => s.queueCampaignChange);

  // Cross-platform aggregator — replaces the Google-store's
  // getOptimizedCampaigns + getTotalCurrentBudget. Filters Meta
  // candidates on CBO + daily eligibility (defense in depth — the
  // FlowerButton surface already prevents staging ineligible toggles).
  const { entries: optimizedCampaignEntries, totalCurrentBudget: poolTotalCurrentBudget } =
    useOptimizedCampaigns();

  const totalBudget = useAccountSettingsStore((state) => state.totalBudget);
  const setTotalBudget = useAccountSettingsStore((state) => state.setTotalBudget);
  const useConversionBasedRoas = useAccountSettingsStore((state) => state.useConversionBasedRoas);
  const revenuePerClick = useAccountSettingsStore((state) => state.revenuePerClick);
  const minDensity = useAccountSettingsStore((state) => state.minDensity);
  const setMinDensity = useAccountSettingsStore((state) => state.setMinDensity);
  const targetAbsDensityChange = useAccountSettingsStore((state) => state.targetAbsDensityChange);
  const setTargetAbsDensityChange = useAccountSettingsStore((state) => state.setTargetAbsDensityChange);
  // `originalSettings?.totalBudget` is what the old `originalTotalBudget`
  // referred to — the last-saved baseline for the auto-sync gate.
  const originalTotalBudget = useAccountSettingsStore((state) => state.originalSettings?.totalBudget ?? null);

  const { memberUuid } = useAuthStore();

  // Phase 0.5 cross-platform currency guard. `isMixed` blocks the
  // Optimize button + renders an Alert; `currency` is sent on the
  // request body so the backend's `_validate_campaign_currencies`
  // can confirm every campaign matches.
  const poolCurrency = usePoolCurrency();

  const [showResults, setShowResults] = useState(false);
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const [changesApplied, setChangesApplied] = useState(false);
  const [devMode, setDevMode] = useState(debug.isDev());
  // Top-level response diagnostics (not per-campaign). null when no
  // imputation happened on the last run.
  const [imputationDiagnostics, setImputationDiagnostics] = useState<{
    imputedMeanRoas: number;
    nImputedCampaigns: number;
  } | null>(null);

  // Allocatable Budget tooltip — hover/focus-driven info popover
  // anchored to the card title. Shared hook handles the 200ms
  // close-delay so the user can transit from the icon to the popover.
  const {
    isOpen: showAllocatableBudgetTooltip,
    open: openAllocatableBudgetTooltip,
    scheduleClose: scheduleCloseAllocatableBudgetTooltip,
  } = useTooltipHoverState();

  // Listen for debug mode changes
  useEffect(() => {
    const updateMode = () => {
      const currentMode = debug.isDev();
      setDevMode(currentMode);
      console.log('BudgetOptimizer: Mode updated to', currentMode ? 'DEVELOPMENT' : 'PRODUCTION');
    };

    // Subscribe to mode changes
    const unsubscribe = debug.onModeChange(updateMode);

    // Also update immediately in case mode was changed before this component mounted
    updateMode();

    return unsubscribe;
  }, []);

  // The legacy dev-mount seed from GET /config/optimization is gone — both
  // params it seeded (targetAbsDensityChange, minDensity) graduated to
  // persisted account-level settings hydrated by appConfigService.loadAppConfig.
  // User-saved values (or their defaults in DEFAULT_ACCOUNT_SETTINGS, which
  // match the backend's production constants) win.

  // Track whether initial configuration has been loaded (to prevent re-loading on campaign updates)
  const hasLoadedInitialConfig = useRef(false);
  // Snapshot of the optimization pool at the moment Run Optimization
  // was clicked. Drives result-row platform tagging + Apply routing
  // so a pool change between Run and Apply (toggle, refetch,
  // eligibility flip) can't desync the table from the request.
  const lastRunPoolRef = useRef<OptimizedCampaignEntry[]>([]);
  // Track when config is ready for auto-sync (after initial load completes)
  const [isConfigReady, setIsConfigReady] = useState(false);

  // Initialize campaign settings when campaigns are loaded.
  // An empty array MUST flow through so the store can drop stale
  // entries (e.g. after a Google Ads account unlink). The Google
  // store's `initializeCampaignSettings` rebuilds the Map from
  // the incoming payload — calling it with [] is the explicit
  // clear. Parity with the Account-page Meta seed.
  useEffect(() => {
    initializeCampaignSettings(campaigns);
  }, [campaigns, initializeCampaignSettings]);

  // Load saved configuration ONLY ONCE after campaign settings are initialized
  // This prevents reloading (and overwriting local changes) when campaigns update due to optimistic updates
  useEffect(() => {
    if (campaigns.length > 0 && !hasLoadedInitialConfig.current) {
      // Small delay to ensure campaign settings are initialized first
      const timer = setTimeout(async () => {
        await loadAppConfig();
        hasLoadedInitialConfig.current = true;
        setIsConfigReady(true);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [campaigns]);

  // Auto-update total budget when campaign selection changes (only if no manual override).
  // poolTotalCurrentBudget is already cent-rounded inside the hook.
  const totalCurrentBudget = poolTotalCurrentBudget;
  const [hasManualBudgetOverride, setHasManualBudgetOverride] = useState(false);
  // Tracks whether we've already done the one-time "restore-from-saved" override
  // detection. After that runs, hasManualBudgetOverride is driven only by user
  // actions (handleBudgetChange / handleResetToCurrentTotal) and the alignment
  // check below — never by the saved value again, so Reset and subsequent
  // campaign toggles can resume auto-sync without being clobbered.
  const overrideInitializedRef = useRef(false);

  useEffect(() => {
    if (!isConfigReady) return; // Wait for initial config to load first

    // One-time restore of the manual-override flag from the saved configuration.
    // Without this, the auto-sync at the bottom would immediately overwrite a
    // saved override value with the calculated campaign sum on first load.
    if (!overrideInitializedRef.current) {
      overrideInitializedRef.current = true;
      if (originalTotalBudget !== null &&
          !isBudgetAligned(originalTotalBudget, totalCurrentBudget)) {
        setHasManualBudgetOverride(true);
        return; // skip auto-sync until override state propagates
      }
    }

    // Clear manual override when the user's intended budget matches the campaign
    // sum — either because they typed a matching value, clicked Reset, or
    // adjusted campaigns to align with their typed total. This lets auto-sync
    // resume and track subsequent campaign-sum changes.
    if (hasManualBudgetOverride &&
        isBudgetAligned(totalBudget, totalCurrentBudget)) {
      setHasManualBudgetOverride(false);
    }

    // Auto-sync totalBudget to match campaign selection changes
    // Only if no manual override is set
    // Note: When totalCurrentBudget is 0 (no campaigns enabled), we intentionally
    // don't call setTotalBudget - there's nothing to allocate in that state
    if (totalCurrentBudget > 0 && !hasManualBudgetOverride) {
      setTotalBudget(totalCurrentBudget);
    }
  }, [isConfigReady, originalTotalBudget, totalCurrentBudget, hasManualBudgetOverride, totalBudget, setTotalBudget]);

  const optimizedCampaigns = optimizedCampaignEntries;
  const hasOptimizedCampaigns = optimizedCampaigns.length > 0;
  // Per-campaign min-density is only feasible up to 1/N (sum_of_mins must
  // be ≤ 1). Guard against div-by-zero when the dev panel renders before any
  // campaign is selected — defaults to 1.0 (full pool), a sane displayed
  // upper bound that the send-boundary clamp will tighten once N > 0.
  const minDensityFeasibleMax = 1 / Math.max(1, optimizedCampaigns.length);

  const handleRunOptimization = async () => {
    try {
      clearError();
      // Cross-platform validation. Inlined here because
      // validateBudgetConfiguration() lives on the Google store and
      // only reads Google candidates — Phase 1c moved the candidate
      // pool to useOptimizedCampaigns (mixed). Mirrors the Google
      // store's three checks: total budget > 0, total minimum
      // allocations ≤ 100%, ≥1 optimized candidate. The min-allocation
      // sum is Google-only because `minimumAllocation` is a Google-side
      // per-campaign field; Meta has no equivalent surface yet.
      const validationErrors: string[] = [];
      if (totalBudget <= 0) {
        validationErrors.push('Total budget must be greater than zero');
      }
      const totalMinimumAllocations = getTotalMinimumAllocations();
      if (totalMinimumAllocations > 100) {
        validationErrors.push(
          `Total minimum allocations (${totalMinimumAllocations.toFixed(1)}%) exceed 100%`
        );
      }
      if (optimizedCampaigns.length === 0) {
        validationErrors.push('Please select at least one campaign for budget optimization');
      }
      if (validationErrors.length > 0) {
        setError(validationErrors.join('\n'));
        return;
      }

      setOptimizing(true);
      setChangesApplied(false); // Reset changes applied state for new optimization
      // Dev-mode-only handler — the Run button is gated on `devMode &&
      // hasOptimizedCampaigns`. The endpoint exists in production, but no
      // production UI surface calls it.
      debug.log('BudgetOptimizer: Running optimization (dev mode)', config);

      // Snapshot the pool composition at request time. The pool can
      // shift between Run and Apply — user toggles a campaign, a
      // refetch updates eligibility/status, etc. — and the
      // results-table + Apply path index into the array by position.
      // If we re-read live `optimizedCampaigns` at Apply time, an
      // index that was Meta at Run could resolve to Google after the
      // pool changes, routing budgets to the wrong store. Capture
      // once, use everywhere downstream until the next Run.
      const runPool: OptimizedCampaignEntry[] = optimizedCampaigns.map((c) => ({ ...c }));
      lastRunPoolRef.current = runPool;
      const campaignIds = runPool.map(c => c.campaignId);
      // Per-entry platform tag aligned to `campaigns_to_optimize`. The
      // backend `BudgetAllocationRequest` model_validator enforces
      // equal length and rejects duplicate (campaign_id, platform)
      // pairs — same campaign id on different platforms is structurally
      // allowed (separate ID namespaces).
      const platforms: OptimizerPlatform[] = runPool.map(c => c.platform);
      // Clamp at the send boundary so the request always carries valid values
      // even if the user typed an empty / negative / out-of-range number into
      // the dev input. HTML5 min/max/step on <input type="number"> only hint
      // the browser; they do not enforce in JS.
      const safeRate = clampNumericInput(
        targetAbsDensityChange, 0.01, 1, PROD_TARGET_ABS_DENSITY_CHANGE,
      );
      // The simplex floor is only feasible when sum_of_mins ≤ 1, i.e.,
      // per-campaign min ≤ 1/N. The backend now clamps to this bound and
      // logs a warning, but clamping at the send boundary keeps the request
      // honest about what the algorithm will actually use.
      const safeMinDensity = clampNumericInput(
        minDensity, 0, minDensityFeasibleMax, PROD_MIN_DENSITY,
      );
      const minDensities = runPool.map(() => safeMinDensity);

      // Per-campaign starting budgets. Prefer the user's staged manualBudget
      // when present, otherwise the current (DB-baseline) value. Sending
      // these explicitly lets preview-mode runs honor unsaved UI changes
      // (e.g., bumping a campaign's budget locally and re-running) — without
      // them, the backend would normalize stale DB proportions and scale by
      // the request's possibly-different campaign_global_budget, producing a
      // reference-frame mismatch (old proportions × new pool).
      //
      // Cross-platform collision: the dict is keyed by campaign_id only.
      // Google + Meta numeric IDs share a namespace shape (bare digits),
      // so the same id COULD appear on both platforms. The backend's
      // request model allows duplicate ids across platforms (rejecting
      // only duplicate `(campaign_id, platform)` pairs), but the
      // request-side `current_budgets` dict can't disambiguate, so the
      // second entry would silently overwrite the first. We fail-closed
      // here: refuse the run and surface a clear error so the user can
      // unlink one account or contact support. No in-the-wild collision
      // observed yet; if this becomes common we'd add a tuple-keyed
      // sibling field on the request.
      const currentBudgets: Record<string, number> = {};
      const collidingIds: string[] = [];
      for (const c of runPool) {
        if (Object.prototype.hasOwnProperty.call(currentBudgets, c.campaignId)) {
          collidingIds.push(c.campaignId);
          continue;
        }
        currentBudgets[c.campaignId] = c.manualBudget ?? c.currentBudget;
      }
      if (collidingIds.length > 0) {
        const unique = Array.from(new Set(collidingIds)).join(', ');
        throw new Error(
          `Cross-platform campaign id collision detected (${unique}). ` +
          `The same numeric id is in the optimization pool on more than one platform — ` +
          `the request can't disambiguate per-campaign starting budgets. ` +
          `Unlink one of the colliding accounts and retry, or contact support.`,
        );
      }

      // Source ROAS inputs from the same store fields the campaign-card
      // display uses (Account-page settings) so the optimizer's gradient
      // and the displayed per-campaign ROAS are computed coherently:
      //   - has_conversion_tracking, has_revenue_per_conversion_tracking ←
      //     config.useConversionBasedRoas (kept paired; mode 3 of the
      //     backend, with has_revenue_per_conversion_tracking=false, requires
      //     a revenue_per_conversion value that we have no UI for).
      //   - revenue_per_click ← user's Account-page value, falling back to
      //     ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK (matches `utils/roas.ts`).
      //
      // With this combination, the backend's `compute_daily_roas` (in
      // `src/flouri/google_ads_data_processing/main.py`) does per-row mode
      // selection — for each campaign-day, prefers actual conversions_value
      // when present and falls back to clicks × revenue_per_click when not.
      // That matches the card-display logic exactly, so what the user sees
      // on the cards and what the optimizer's gradient computes from line
      // up regardless of whether conversion tracking has accumulated data
      // for every campaign.
      const requestData = {
        // Phase 0.5 — backend requires currency_code. Fall back to 'USD'
        // when the pool's currency couldn't be determined (no usable
        // credential row carrying account_currency). The backend's
        // per-campaign validation silently skips campaigns whose currency
        // resolves to null, so this fallback is a soft default rather than
        // a guess that'll trigger a 422. Mixed-currency pools never reach
        // here — the button is disabled upstream.
        currency_code: poolCurrency.currency ?? 'USD',
        campaign_global_budget: totalBudget,
        target_abs_density_change: safeRate,
        min_densities: minDensities,
        campaigns_to_optimize: campaignIds,
        // Phase 1a-pivot — per-entry platform tags aligned to
        // `campaigns_to_optimize`. The backend partitions on this and
        // dispatches Google entries to clientUpdateGoogle, Meta entries
        // to clientUpdateMeta. Absent → backend defaults to all-Google
        // for backwards-compat, but we always send it explicitly now
        // that the candidate pool is mixed.
        platforms,
        has_conversion_tracking: useConversionBasedRoas,
        has_revenue_per_conversion_tracking: useConversionBasedRoas,
        revenue_per_click:
          revenuePerClick ?? ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK,
        revenue_per_impression: null,
        revenue_per_conversion: null,
        dow_wise: false,
        implement_opt_budgets: config.devImplementOptBudgets,
        current_budgets: currentBudgets,
      };

      debug.log('BudgetOptimizer: Detailed request data:', requestData);
      debug.log('BudgetOptimizer: Campaign IDs:', campaignIds);
      debug.log('BudgetOptimizer: Min densities:', minDensities);
      debug.log('BudgetOptimizer: Config state:', config);

      const response = await optimizationApi.runBudgetAllocation(requestData);

      debug.log('BudgetOptimizer: Optimization response', response);

      // Capture NA-injection diagnostics from the top-level response. Null
      // when no imputation happened (all campaigns measured, or none had
      // clicks for the algorithm to compute a measured mean from).
      setImputationDiagnostics(
        response.imputed_mean_roas !== null && response.imputed_mean_roas !== undefined
          ? {
              imputedMeanRoas: response.imputed_mean_roas,
              nImputedCampaigns: response.n_imputed_campaigns,
            }
          : null,
      );

      // Process results
      if (response.allocation_adjusted) {
        const results: BudgetAllocationResult[] = campaignIds.map((campaignId, index) => {
          // Use the value WE SENT as current_budgets[campaignId] as the
          // "Previous Budget" baseline so the displayed delta matches what
          // the optimizer actually computed against. campaigns[i].budget
          // is the server/saved value, which can diverge from the store's
          // staged manualBudget — using it would show misleading deltas
          // for users who ran optimization against unsaved edits.
          //
          // Match by index, not by `find(c.campaignId === campaignId)`:
          // a cross-platform numeric-id collision (Google + Meta) would
          // otherwise pick whichever entry .find encounters first,
          // which may not be the one this index slot was built from.
          // Use the request-time snapshot (`runPool`) rather than the
          // live `optimizedCampaigns` so a pool change between Run and
          // result processing can't shift the indices.
          const optimizedCampaign = runPool[index];
          const currentBudget =
            optimizedCampaign?.manualBudget ?? optimizedCampaign?.currentBudget ?? 0;
          // Compute the proposed budget from the allocation × total. The
          // backend only populates `budget_updates` when implement_opt_budgets
          // is true AND the Google Ads write succeeded, so in preview mode
          // we'd see $0 if we relied on it. Prefer the backend's cent-rounded
          // value when present (it's the authoritative implemented amount);
          // otherwise compute from allocation × totalBudget and cent-round
          // the same way the backend would have.
          const allocationFraction = response.allocation_adjusted![index];
          const computedBudget =
            Math.round(allocationFraction * totalBudget * 100) / 100;
          const optimizedBudget =
            response.budget_updates?.[campaignId] ?? computedBudget;
          const allocationPercentage = allocationFraction * 100;

          return {
            campaignId,
            currentBudget,
            optimizedBudget,
            allocationPercentage,
            change: optimizedBudget - currentBudget,
            changePercentage: currentBudget > 0
              ? ((optimizedBudget - currentBudget) / currentBudget) * 100
              : 0,
          };
        });

        setOptimizationResults(results);
        setShowResults(true);

        // Trigger data sync to refresh campaign data after budget update
        if (response.budgets_implemented) {
          if (memberUuid) {
            try {
              debug.log('BudgetOptimizer: Triggering data sync after budget update');
              await googleAdsApi.triggerDataSync(memberUuid, 30);
              debug.log('BudgetOptimizer: Data sync triggered successfully');
            } catch (syncError) {
              debug.warn('BudgetOptimizer: Failed to trigger data sync', syncError);
              // Don't fail the optimization if sync fails
            }
          } else {
            debug.warn('BudgetOptimizer: Budgets were implemented but cannot trigger data sync - memberUuid is missing');
            // This may result in stale campaign data in the UI until manual refresh
          }
        }
      }
    } catch (err) {
      debug.error('BudgetOptimizer: Optimization failed', err);
      setError(err instanceof Error ? err.message : 'Budget optimization failed');
    } finally {
      setOptimizing(false);
    }
  };

  const handleBudgetChange = (newBudget: number) => {
    setTotalBudget(newBudget);
    setHasManualBudgetOverride(true);
  };

  const handleResetToCurrentTotal = () => {
    setTotalBudget(totalCurrentBudget);
    setHasManualBudgetOverride(false);
  };

  // Note: handleSaveConfiguration removed - saving is now handled by
  // AccountSaveChangesBar in the Account page

  const handleApplyChanges = async () => {
    if (!optimizationResults) return;

    try {
      setIsApplyingChanges(true);
      clearError();

      debug.log('BudgetOptimizer: Applying optimization results to campaigns');

      // Apply optimized budgets to the right store per platform. Match
      // by index against the **request-time snapshot** (`runPool` /
      // `lastRunPoolRef.current`) — NOT live `optimizedCampaigns`. If
      // the pool shifted between Run and Apply (toggle, refetch,
      // eligibility flip), live indices could resolve to a different
      // platform than the result row was built for, routing budgets to
      // the wrong store.
      //
      // Google: writes through the Google store's manualBudgetChanges
      // Set → preSave flushes via optimizationApi.updateManualBudgets.
      // Meta: writes through the Meta store's manualBudgetChanges Set
      // AND queues a CampaignPropertyChange so pendingChangesStore's
      // saveAllChanges dispatches via /meta/batch-update budget_changes.
      // Meta entries don't have a preSave hook; the queue is the only
      // path to the Meta API.
      const runPool = lastRunPoolRef.current;
      optimizationResults.forEach((result, index) => {
        const entry = runPool[index];
        if (!entry) return;
        if (entry.platform === 'meta_ads') {
          setManualBudgetMeta(result.campaignId, result.optimizedBudget);
          queueCampaignChange({
            campaignId: result.campaignId,
            property: 'budget',
            oldValue: result.currentBudget,
            newValue: result.optimizedBudget,
            platform: 'meta_ads',
          });
        } else {
          setManualBudgetGoogle(result.campaignId, result.optimizedBudget);
        }
      });

      // Save the configuration with the new budgets. Two flushes:
      // 1. pendingChangesStore.saveAllChanges → metaAdsApi.batchUpdate
      //    for the queued Meta budget entries. Skipped when there are
      //    no Meta entries (Google-only run hits the path below only).
      // 2. saveAppConfig → Google preSave (manual budget endpoint) +
      //    the regular slice writes (carries the new Meta budgets too
      //    via the Meta slice's `budget` field, so a reload sees
      //    them even when the Meta batch itself partially failed).
      // Use the request-time snapshot — same reason as the routing
      // loop above. A pool change between Run and Apply mustn't shift
      // the "do we need to dispatch via pendingChangesStore?" decision.
      const hasMetaEntries = runPool.some(c => c.platform === 'meta_ads');
      if (hasMetaEntries) {
        // `usePendingChangesStore` is already top-level imported; reach
        // through `.getState()` for an event-handler-time read instead
        // of subscribing via the hook (we don't want a re-render here).
        const result = await usePendingChangesStore.getState().saveAllChanges();
        if (!result.success) {
          throw new Error(result.errors.join('\n'));
        }
      }
      await saveAppConfig();
      
      // Refetch campaigns to show updated budgets in UI
      if (onCampaignsUpdate) {
        debug.log('BudgetOptimizer: Refreshing campaign data');
        await onCampaignsUpdate();
      }
      
      setChangesApplied(true);
      debug.log('BudgetOptimizer: Changes applied and saved successfully');
      
    } catch (err) {
      debug.error('BudgetOptimizer: Failed to apply changes', err);
      setError(err instanceof Error ? err.message : 'Failed to apply optimization changes');
    } finally {
      setIsApplyingChanges(false);
    }
  };


  if (campaigns.length === 0) {
    return null; // Don't show if no campaigns
  }

  return (
    <div className="space-y-6" key={`budget-optimizer-${devMode}`}>
      {/* Total Budget Section - with slide-down animation. The outer
          `relative` wrapper sits OUTSIDE the `overflow-hidden` grid so the
          info popover can extend above the card without being clipped. */}
      <div className="relative">
        {showAllocatableBudgetTooltip && (
          <div
            id="allocatable-budget-tooltip"
            role="tooltip"
            className="tooltip-info-popover"
            onMouseEnter={openAllocatableBudgetTooltip}
            onMouseLeave={scheduleCloseAllocatableBudgetTooltip}
          >
            <AllocatableBudgetTooltipContent />
          </div>
        )}
        <div
          className="grid transition-[grid-template-rows] duration-300 ease-out"
          style={{ gridTemplateRows: hasOptimizedCampaigns ? '1fr' : '0fr' }}
        >
        <div className="overflow-hidden">
          <div className="flex justify-center">
            <Card className="!border-2 !border-blue-300 bg-blue-50 shadow-sm max-w-2xl w-full">
              <Card.Header>
                <h3 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <span className="text-4xl text-green-600 font-bold">$</span>
                  <span>Total Campaign Budget<span className="block md:inline"> to Flouri Optimize</span></span>
                  <button
                    type="button"
                    aria-label="About allocatable budget"
                    // Only point at the tooltip id while it's actually
                    // in the DOM. The tooltip is conditionally rendered
                    // on `showAllocatableBudgetTooltip` (line 601); an
                    // `aria-describedby` referencing a non-existent id
                    // is invalid ARIA and can confuse screen readers
                    // (Copilot review on PR #206).
                    aria-describedby={showAllocatableBudgetTooltip ? "allocatable-budget-tooltip" : undefined}
                    onMouseEnter={openAllocatableBudgetTooltip}
                    onMouseLeave={scheduleCloseAllocatableBudgetTooltip}
                    onFocus={openAllocatableBudgetTooltip}
                    onBlur={scheduleCloseAllocatableBudgetTooltip}
                    className="ml-1 text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-full"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </button>
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Select campaigns above to be optimized.
                </p>
              </Card.Header>
              <Card.Body>
                <div className="space-y-4">
                  <div>
                    <BudgetBar
                      value={totalBudget}
                      originalValue={totalCurrentBudget}
                      onChange={handleBudgetChange}
                      onReset={handleResetToCurrentTotal}
                      label="Overall Allocatable Budget ($):"
                      width="w-full"
                    />
                  </div>

                  <div className="bg-white p-3 rounded border">
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>Enabled campaigns selected for optimization: {optimizedCampaigns.length}</div>
                      <div>Total current budget of selected and enabled campaigns: {formatCurrency(totalCurrentBudget)}</div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="error" onDismiss={clearError}>
          <pre className="whitespace-pre-wrap">{error}</pre>
        </Alert>
      )}

      {/* Phase 0.5 — cross-platform currency guard. Block the optimizer
          when the flouri-optimized pool spans multiple currencies; the
          simplex treats budget numbers as homogeneous scalars, so mixing
          (e.g.) USD and EUR campaigns produces nonsensical allocations.
          The selector + per-currency state would be the way to support
          multi-currency members, but for Phase 1 we're deferring that
          (see `docs/META_BUDGET_OPTIMIZATION_ACTIVATION.md` "Deferred"
          section). Disabling the button is the honest stopgap. */}
      {devMode && hasOptimizedCampaigns && poolCurrency.isMixed && (
        <Alert variant="warning">
          <p className="font-medium">
            Mixed-currency accounts aren't supported yet.
          </p>
          <p className="text-sm mt-1">
            Flouri budget optimization requires all selected campaigns to use
            the same currency. You have campaigns in{' '}
            {poolCurrency.currenciesInPool.join(', ')}.
          </p>
        </Alert>
      )}

      {/* Budget Optimization Action - Development Only */}
      {devMode && hasOptimizedCampaigns && (
        <Card className="border-yellow-200 bg-yellow-50">
          <Card.Header>
            <h3 className="text-lg font-medium text-yellow-800">
              🎯 Budget Allocation Optimization
            </h3>
            <p className="text-sm text-yellow-700 mt-1">
              Optimize budget allocation across selected campaigns
            </p>
          </Card.Header>
          <Card.Body>
            <div className="text-center">
              <Button
                onClick={handleRunOptimization}
                disabled={isOptimizing || poolCurrency.isMixed}
                loading={isOptimizing}
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                🚀 Run Budget Optimization
              </Button>
              <p className="text-xs text-gray-600 mt-2">
                {config.devImplementOptBudgets
                  ? 'Optimized budgets will be applied to your Google Ads + Meta Ads campaigns.'
                  : 'Preview only — no changes will be written to Google Ads or Meta Ads. Toggle “Implement optimized budgets” below to commit.'}
              </p>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Development-only sections — editable simplex params for testing */}
      {devMode && (
        <Card className="border-blue-200 bg-blue-50">
          <Card.Header>
            <h3 className="text-lg font-medium text-blue-800">🔧 Development Controls</h3>
            <p className="text-xs text-blue-700 mt-1">
              Per-member simplex parameters. Saved to your account settings
              on the next Save, and read by the weekly Cloud Run optimization
              job per member. Falls back to the production defaults in{' '}
              <code>config.json</code> when unset.
            </p>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4 text-sm">
              <div>
                <label
                  htmlFor="target-abs-density-change"
                  className="block font-medium text-blue-900 mb-1"
                >
                  Target absolute density change (rate)
                </label>
                <Input
                  id="target-abs-density-change"
                  type="number"
                  min={0.01}
                  max={1}
                  step={0.01}
                  value={targetAbsDensityChange}
                  onChange={(e) => {
                    // Use valueAsNumber + skip non-finite to keep NaN out
                    // of the controlled component's value prop. Number("")
                    // returns 0, but `<input type=number>`.valueAsNumber
                    // returns NaN for empty input, which we ignore — the
                    // visible field briefly shows empty but state stays at
                    // the last valid value, so the next render restores it.
                    const v = e.target.valueAsNumber;
                    if (Number.isFinite(v)) {
                      setTargetAbsDensityChange(v);
                    }
                  }}
                  aria-describedby="target-abs-density-change-help"
                />
                <span
                  id="target-abs-density-change-help"
                  className="text-xs text-gray-600"
                >
                  How much of the allocation density to shift toward
                  higher-weighted campaigns this run. Range 0.01–1.0.
                </span>
              </div>

              <div>
                <label
                  htmlFor="min-density"
                  className="block font-medium text-blue-900 mb-1"
                >
                  Min density (per-campaign floor)
                </label>
                <Input
                  id="min-density"
                  type="number"
                  min={0}
                  max={minDensityFeasibleMax}
                  step={0.01}
                  value={minDensity}
                  onChange={(e) => {
                    // Same valueAsNumber + finite check pattern as the rate
                    // input above — prevents NaN from entering the
                    // controlled component value when the field is empty.
                    const v = e.target.valueAsNumber;
                    if (Number.isFinite(v)) {
                      setMinDensity(v);
                    }
                  }}
                  aria-describedby="min-density-help"
                />
                <span id="min-density-help" className="text-xs text-gray-600">
                  Floor on each campaign's allocation share. Feasible max is{' '}
                  1 / N campaigns = {minDensityFeasibleMax.toFixed(3)}
                  {optimizedCampaigns.length > 0 &&
                    ` (with ${optimizedCampaigns.length} selected)`}
                  ; higher values get clamped at the send boundary.
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="dev-implement-opt-budgets"
                  checked={config.devImplementOptBudgets}
                  onChange={(checked) =>
                    setConfig({ devImplementOptBudgets: checked })
                  }
                />
                <label
                  htmlFor="dev-implement-opt-budgets"
                  className="font-medium text-blue-900 cursor-pointer"
                >
                  Implement optimized budgets
                </label>
              </div>
              <p className="text-xs text-gray-600 -mt-2 ml-6">
                When unchecked, the run is preview-only and won't mutate
                Google Ads or Meta Ads.
              </p>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Optimization Results - Development Only */}
      {devMode && showResults && optimizationResults && (
        <Card className="border-green-200 bg-green-50">
          <Card.Header>
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-green-800">
                  ✅ Optimization Complete
                </h3>
                <p className="text-sm text-green-700 mt-1">
                  {changesApplied 
                    ? "Budget changes have been applied to your campaigns"
                    : "Review the optimized budget allocation below"
                  }
                </p>
              </div>
              <Button variant="ghost" onClick={() => setShowResults(false)}>
                ✕
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {imputationDiagnostics && (
              <div className="mb-4 p-3 rounded border border-amber-200 bg-amber-50 text-xs text-amber-900">
                <strong>NA imputation:</strong>{' '}
                {imputationDiagnostics.nImputedCampaigns} campaign(s) had no
                measurable activity (clicks = 0); imputed ROAS{' '}
                <code className="font-mono">
                  {imputationDiagnostics.imputedMeanRoas.toFixed(4)}
                </code>{' '}
                (mean of measured campaigns) so their gradient is neutral
                and they keep current share rather than being squeezed to
                the floor.
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase py-2">Campaign</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase py-2">Previous Budget</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase py-2">New Budget</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase py-2">Change</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {optimizationResults.map((result, index) => {
                    // Prefer the cross-store entry's name (works for both
                    // Google + Meta — `campaigns` prop is Google-only).
                    // Fall back to the Google-only `campaigns` prop for
                    // resilience if the optimized-campaign set drifted
                    // mid-render. Match by index against the
                    // **request-time snapshot** (`lastRunPoolRef`) so a
                    // pool change between Run and table re-render
                    // (toggle, refetch, eligibility flip, id-collision
                    // resolution) can't shift the indices and label a
                    // result row with the wrong platform.
                    const entry = lastRunPoolRef.current[index];
                    const fallbackCampaign = campaigns.find(
                      c => c.campaign_id === result.campaignId,
                    );
                    const displayName =
                      entry?.campaignName || fallbackCampaign?.campaign_name || result.campaignId;
                    return (
                      <tr key={`${entry?.platform ?? 'unknown'}:${result.campaignId}`}>
                        <td className="py-2 text-sm text-gray-900">
                          {displayName}
                          {entry?.platform === 'meta_ads' && (
                            <span className="ml-2 text-xs text-gray-500">(Meta)</span>
                          )}
                        </td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {formatCurrency(result.currentBudget)}
                        </td>
                        <td className="py-2 text-sm font-medium text-green-600 text-right">
                          {formatCurrency(result.optimizedBudget)}
                        </td>
                        <td className="py-2 text-right">
                          <div className={cn(
                            'text-sm font-medium',
                            result.change > 0 ? 'text-green-600' : result.change < 0 ? 'text-amber-600' : 'text-gray-600'
                          )}>
                            {result.change > 0 ? '+' : ''}{formatCurrency(result.change)}
                            {Math.abs(result.changePercentage) > 0.01 && (
                              <span className="text-xs ml-1">
                                ({result.changePercentage > 0 ? '+' : ''}{result.changePercentage.toFixed(1)}%)
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Apply Changes Button — only shown when the user opted in to
                implementing changes (the dev checkbox above). In preview
                mode the panel is intentionally read-only: the Apply path
                writes through to Google Ads via saveConfiguration →
                updateManualBudgets, which would silently violate the
                "preview = no side effects" contract. */}
            {!changesApplied && config.devImplementOptBudgets && (
              <div className="mt-4 pt-4 border-t border-green-200">
                <div className="flex justify-center">
                  <Button
                    onClick={handleApplyChanges}
                    disabled={isApplyingChanges}
                    loading={isApplyingChanges}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
                  >
                    {isApplyingChanges ? 'Applying Changes...' : '✅ Apply Budget Changes'}
                  </Button>
                </div>
                <p className="text-xs text-green-600 text-center mt-2">
                  This will update your campaign budgets and save the configuration
                </p>
              </div>
            )}
            {!changesApplied && !config.devImplementOptBudgets && (
              <div className="mt-4 pt-4 border-t border-green-200">
                <p className="text-xs text-gray-600 text-center">
                  Preview mode — no changes were written. Check{' '}
                  <em>Implement optimized budgets</em> above and re-run
                  to commit.
                </p>
              </div>
            )}
            
            {/* Applied Confirmation */}
            {changesApplied && (
              <div className="mt-4 pt-4 border-t border-green-200">
                <div className="flex items-center justify-center space-x-2 text-green-700">
                  <span className="text-lg">✅</span>
                  <span className="font-medium">
                    Budget changes have been applied successfully!
                  </span>
                </div>
                <p className="text-xs text-green-600 text-center mt-1">
                  Your campaigns now have the optimized budgets
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Note: Save button removed - the Account page now has a unified SaveChangesBar
          that handles all pending changes including budget/name updates */}
    </div>
  );
};